import { createFileRoute } from '@tanstack/react-router'

const MorphologyPage = () => {
  return (
    <div className="mt-6 flex-col pb-6 sm:w-full">
      <h1 className="mb-2 text-xl font-bold text-gray-800">Seed Morphology</h1>

      <h3 className="mb-2 text-lg font-bold text-gray-800">
        General Information on Seed and Fruit
      </h3>

      <p className="mb-2">
        Data and images presented here were captured by the Millennium Seed
        Bank's Seed Morphologist{' '}
        <a href="http://www.kew.org/scihort/directory/people/Stuppy_Wolfgang.html">
          Wolfgang Stuppy
        </a>{' '}
        assisted by Gemma Hoyle, Elly Vaes and Hanny Morales.
      </p>

      <h3 className="mb-2 text-lg font-bold text-gray-800">Fruit size</h3>
      <p className="mb-2">
        (l × w × t) The size of the fruit is measured in length (l) × width (w)
        × thickness (t). Length refers to the dimension from the point of
        attachment to the tip of the fruit (or the opposite end). Width and
        thickness are two lateral dimensions which may or may not be identical,
        e.g. in a compressed fruit the larger lateral diameter would be referred
        to as width and the smaller one as thickness.
      </p>

      <h3 className="mb-2 text-lg font-bold text-gray-800">Fruit type:</h3>
      <p className="mb-2">Fruit type is based on Spjut 1994.</p>

      <div className="mb-2">
        <ul className="">
          <li>achene</li>
          <li>achenetum</li>
          <li>amphisarcum</li>
          <li>baccarium</li>
          <li>baccetum</li>
          <li>berry</li>
          <li>camara</li>
          <li>camaretum</li>
          <li>camarium</li>
          <li>capsule</li>
          <li>caryopsis with lemma and palea attached</li>
          <li>caryopsis without lemma and palea attached</li>
          <li>caryopsis</li>
          <li>coccarium</li>
          <li>coccetum</li>
          <li>coccum</li>
          <li>compound cone</li>
          <li>craspedium </li>
          <li>cypsela</li>
          <li>druparium</li>
          <li>drupe</li>
          <li>drupetum</li>
          <li>dry dehiscent multiple fruit</li>
          <li>dry indehiscent multiple fruit</li>
          <li>fleshy dehiscent multiple fruit</li>
          <li>follicarium</li>
          <li>follicetum</li>
          <li>follicle</li>
          <li>glandetum</li>
          <li>glans</li>
          <li>hesperidium</li>
          <li>legume</li>
          <li>loculicidal capsule</li>
          <li>lomentarium</li>
          <li>lomentetum</li>
          <li>lomentum</li>
          <li>microbasarium</li>
          <li>nut</li>
          <li>pepo</li>
          <li>polachenarium</li>
          <li>pomarium</li>
          <li>pome</li>
          <li>poricidal capsule</li>
          <li>pseudodrupe</li>
          <li>pseudosamara</li>
          <li>pyxidium</li>
          <li>samara</li>
          <li>samaretum</li>
          <li>samarium</li>
          <li>schizocarp</li>
          <li>septicidal capsule</li>
          <li>silicula</li>
          <li>siliqua</li>
          <li>simple cone</li>
          <li>sorosus</li>
          <li>strobilus</li>
          <li>syconium</li>
          <li>syncarpium</li>
          <li>utricle</li>
        </ul>
      </div>
      <h3 className="mb-2 text-lg font-bold text-gray-800">Diaspore:</h3>
      <p className="mb-2">
        This refers to the actual dispersal unit. If a fruit is dehiscent, the
        dispersal unit or diapsore is usually the seed. Indehiscent fruits do
        not open to release their seeds. In these cases, the seeds are
        distributed together with the entire fruit (the pericarp or other floral
        parts, e.g. the calyx) or part of the fruit (e.g. the schizocarpic but
        indehiscent sarmarium of Acer or the segments of a lomentum of some
        legumes). The following options are used: seed, entire fruit, partial
        fruit, segment of lomentum or entire plant.
      </p>

      <h3 className="mb-2 text-lg font-bold text-gray-800">
        Mechanical protection of the seed:
      </h3>
      <p className="mb-2">
        Indehiscent fruits release their seeds to the environment. Seeds from
        dehiscent fruits usually develop hardened seed coats that provide the
        mechanical protection of the seed. In indehiscent fruits other than
        berries, it is the entire fruit wall (pericarp, e.g. in nuts) or part of
        it (usually the inner layer, called endocarp, e.g. in drupes) that
        provides the mechanical protection of the seed. The mechanical
        protection can also be provided by a hard endosperm (e.g. grasses,
        palms, Guaiacum). In grasses the lemma and/or palea can remain attached
        to the grain and provide mechanical protection and the lemma, palea and
        endosperm have been observed together. Rarely (only in
        Cactaceae-Opuntioideae) a woody funiculus wraps around the seeds. The
        options are: seed coat, pericarp, endocarp, calyx, perianth, bracts or
        bracteoles, lemma and palea, lemma, palea and endosperm or funiculus.
      </p>

      <h3 className="mb-2 text-lg font-bold text-gray-800">The seed</h3>
      <p className="mb-2">
        <b>Seed size (l × w × h):</b> The measurements of the seed are given as
        length (l) × width (w) × height (h). Length refers to the distance
        between the chalazal and micropylar end of the seed, width is the
        lateral diameter (the distance between the flanks of the seed) and
        height (thickness) is the distance between the ventral (raphal) side and
        the dorsal side. If the seeds are campylotropous and only have short or
        no raphe, the longest diameter that runs parallel to the axes connecting
        the chalaza and micropyle is taken as the length.
      </p>

      <h3 className="mb-2 text-lg font-bold text-gray-800">The diaspore</h3>
      <p className="mb-2">
        <b>Diaspore size (l × w × t):</b> The size of the diaspore is measured
        in length (l) × width (w) × thickness (t). Length refers to the
        dimension from the point of attachment to the plant to the tip of the
        diaspore (or the opposite end), width and thickness are two lateral
        dimensions which may or may not be identical, e.g. in a compressed
        diaspore the larger lateral diameter is referred to as width and the
        smaller one as thickness.
      </p>

      <h3 className="mb-2 text-lg font-bold text-gray-800">Dispersal aids:</h3>
      <p className="mb-2">
        Refers to anything assisting the diaspore to travel away from the mother
        plant e.g., wings, hairs or a foveolate surface for wind dispersal,
        fleshy parts to attract animals or hooks to attach to them. The options
        are: none, hairs, hooks, wing or foveolate surface.
      </p>

      <h2 className="mb-2 text-lg font-bold text-gray-800">
        Internal Seed Morphology
      </h2>

      <h3 className="mb-2 text-lg font-bold text-gray-800">
        Seed configuration:
      </h3>
      <p className="mb-2">
        It is observed whether the longitudinal axis of the seed is straight or
        bent. All seeds with a bent longitudinal axis (and then usually also
        with bent embryos) are called campylotropous. If the seed has a straight
        axis it is usually anatropous, sometimes hemianatropous. The options
        used are: anatropous, orthotropous (= atropous), hemitropous (=
        hemianatropous), campylotropous, ana-campylotropous,
        ortho-campylotropous, hemi-campylotropous (= hemiana-campylotropous) or
        obcampylotropous.
      </p>

      <h3 className="mb-2 text-lg font-bold text-gray-800">Type of Embryo:</h3>
      <p className="mb-2">
        The type of embryo is based in accordance to Martin's (1946)
        classification. Martin's twelve different embryo types are: basal-broad,
        basal-capitate, basal-lateral, peripheral, axial-linear, axial-dwarf,
        axial-micro, axial-spatulate, axial-investing, axial-bent, axial-folded
        or rudimentary.
      </p>

      <h3 className="mb-2 text-lg font-bold text-gray-800">
        Relative size of embryo:
      </h3>
      <p className="mb-2">
        With respect to the relative size of the embryo (and reciprocally the
        endosperm), Martin (1946) suggested a quantitative classification system
        instead of applying rather vague terms accepting that a small embryo
        implies copious endosperm and copious endosperm implies a small embryo.
        Martin's quantitative system consists of five size-designations
        representing approximately quarter-unit volumetric proportions of embryo
        to endosperm: <b>small</b> (¼ minus), <b>quarter</b> (¼ plus),{' '}
        <b>half</b> (½ plus), <b>dominant</b> (¾ plus), and <b>total</b>. The
        relative size of the embryo is estimated by examining both a
        longitudinal and a cross section of a seed.
      </p>

      <h2 className="mb-2 text-lg font-bold text-gray-800">
        Key References for Morphology Module in SID
      </h2>

      <ul>
        <li>
          Boesewinkel, F.D. and Bouman, F. 1984: The seed: structure. In:
          Embryology of angiosperms (ed. B.M.Johri), pp. 567-610. Springer
          Verlag, Heidelberg.
        </li>
        <li>
          Boesewinkel, F.D. and Bouman, F. 1995: The seed: structure and
          function. In: Seed Development and germination (eds J. Kigel &amp; G.
          Galili), pp. 1-24. Marcel Dekker, New York.
        </li>
        <li>
          Bouman, F. 1984: The ovule. In: Johri, J.B. (ed.): Embryology of the
          angiosperms, pp. 123-157. Springer Verlag, Berlin, Heidelberg, New
          York, Tokyo.
        </li>
        <li>
          Bouman, F. &amp; Boesewinkel, F.D. 1991: The campylotropous ovules and
          seeds, their structure and functions. Bot. Jahrb. Syst. 113(2/3):
          255-270.
        </li>
        <li>
          Corner, E.J.H. 1976: The seeds of dicotyledons. Cambridge University
          Press, Cambridge.
        </li>
        <li>
          Cronquist, A. 1988: The evolution and classification of flowering
          plants. 2nd ed. The New York Botanical Garden, Bronx.
        </li>
        <li>
          Esau, K. 1965: Plant anatomy, 2nd ed. John Wiley &amp; Sons, Inc., New
          York, London, Sydney.
        </li>
        <li>Fahn, A. 1990: Plant anatomy, 4th ed. Pergamon Press, Oxford.</li>
        <li>
          Gunn, C.R. 1981: Seeds of the Leguminosae. — In: Polhill, R.M. &amp;
          Raven, P.H. (eds.), Advances in Legume Systematics. — Int. Legume
          Conf. K, Proc. 1978, V. 2. Min. Agr., Fisheries and Food, Richmond,
          England, pt. 2, pp. 913-925.
        </li>
        <li>
          Gunn, C.R. 1984: Fruits and seeds of genera in the subfamily
          Mimosoideae (Fabaceae). - U.S.D.A. Agric. Res. Serv. Techn. Bull.
          1681: 1-194.
        </li>
        <li>
          Gunn, C.R. 1991: Fruits and seeds of genera in the subfamily
          Caesalpinioideae (Fabaceae). U.S.D.A. Agric. Res. Serv. Techn. Bull.
          1755: 1-408.
        </li>
        <li>
          Johri, B.M., Ambegoakar, K.B. &amp; Srivastava, P.S. 1992: Comparative
          embryology of angiosperms. Vol 1 + 2. Springer Verlag, Berlin,
          Heidelberg, New York, Tokyo.
        </li>
        <li>
          Kapil, R.N., Bor, J. &amp; Bouman, F. 1980: Seed appendages in
          angiosperms. I. Introduction. Bot. Jahrb. Syst. 101(4): 555-573.
        </li>
        <li>
          Martin, A.C. 1946: The Comparative Internal Morphology of Seeds.
          American Midland Naturalist 36, 513-660
        </li>
        <li>
          Roth, I. 1977: Fruits of angiosperms. Gebrüder Bornträger, Berlin
          &amp; Stuttgart. 675 pp.
        </li>
        <li>
          Spjut, R.W. 1994: A systematic treatment of fruit types. Mem. New York
          Bot. Gard. 70:1-182.
        </li>
        <li>
          Vaughan, J.G. 1970: The structure and utilisation of oil seeds.
          Chapman &amp; Hall, London, UK.
        </li>
        <li>
          Werker, E. 1997: Seed anatomy. Gebrüder Bornträger, Berlin, Stuttgart
          (Encyclopedia of plant anatomy, Vol. 10, Part 3.)
        </li>
      </ul>
    </div>
  )
}
/*

<h2>Image Filenames</h2>

<p className='mb-2'>They follow the principle: "Genus_species_#_S_CS", whereby # is the sequential number for images taken for a certain taxon, the letter(s) following the number stand for:</p>
<ul>
  <li>"diaspore" (<b>D</b>) or "diaspores" (<b>DD</b>)</li>
  <li>"fruit" (<b>F</b>) or "fruits" (<b>FF</b>)</li>
  <li>"infructescence" (<b>IN</b>)</li>
  <li>"mericarps" (<b>MC</b>)</li>
  <li>"parent plant" (<b>PP</b>)</li>
  <li>"seed" (<b>S</b>) or "seeds" (<b>SS</b>)</li>
  <li>"seed and fruit" (<b>SF</b>)</li>
  <li>"seeds and fruits" (<b>SSFF</b>)</li>
</ul>
<p className='mb-2'>The last two letters stand for:</p>
<ul>
  <li>"anatomical cross section" (<b>AC</b>)</li>
  <li>"anatomical longitudinal section" (<b>AL</b>)</li>
  <li>"apical view" (<b>AV</b>)</li>
  <li>"basal view" (<b>BV</b>)</li>
  <li>"cross section" (<b>CS</b>)</li>
  <li>"dorsal view" (<b>DV</b>)</li>
  <li>"embryo" (<b>EM</b>)</li>
  <li>"germinating seed" (<b>GE</b>)</li>
  <li>"longitudinal section" (<b>LS</b>)</li>
  <li>"lateral view" (<b>LV</b>)</li>
  <li>"various orientations" (only in combination with SS or FF) (<b>VO</b>)</li>
  <li>"ventral view" (<b>VV</b>)</li>
  <li>"illustration" (<b>IL</b>)</li>
</ul>

<h2 className="mb-2 text-lg font-bold text-gray-800">Key References for Morphology Module in SID</h2>

<ul>
<li>Boesewinkel, F.D. and Bouman, F. 1984: The seed: structure. In: Embryology of angiosperms (ed. B.M.Johri), pp. 567-610. Springer Verlag, Heidelberg.</li>
<li>Boesewinkel, F.D. and Bouman, F. 1995: The seed: structure and function. In: Seed Development and germination (eds J. Kigel &amp; G. Galili), pp. 1-24. Marcel Dekker, New York.</li>
<li>Bouman, F. 1984: The ovule. In: Johri, J.B. (ed.): Embryology of the angiosperms, pp. 123-157. Springer Verlag, Berlin, Heidelberg, New York, Tokyo.</li>
<li>Bouman, F. &amp; Boesewinkel, F.D. 1991: The campylotropous ovules and seeds, their structure and functions. Bot. Jahrb. Syst. 113(2/3): 255-270.</li>
<li>Corner, E.J.H. 1976: The seeds of dicotyledons. Cambridge University Press, Cambridge.</li>
<li>Cronquist, A. 1988: The evolution and classification of flowering plants. 2nd ed. The New York Botanical Garden, Bronx.</li>
<li>Esau, K. 1965: Plant anatomy, 2nd ed. John Wiley &amp; Sons, Inc., New York, London, Sydney.</li>
<li>Fahn, A. 1990: Plant anatomy, 4th ed. Pergamon Press, Oxford.</li>
<li>Gunn, C.R. 1981: Seeds of the Leguminosae. — In: Polhill, R.M. &amp; Raven, P.H. (eds.), Advances in Legume Systematics. — Int. Legume Conf. K, Proc. 1978, V. 2. Min. Agr., Fisheries and Food, Richmond, England, pt. 2, pp. 913-925.</li>
<li>Gunn, C.R. 1984: Fruits and seeds of genera in the subfamily Mimosoideae (Fabaceae). - U.S.D.A. Agric. Res. Serv. Techn. Bull. 1681: 1-194.</li>
<li>Gunn, C.R. 1991: Fruits and seeds of genera in the subfamily Caesalpinioideae (Fabaceae). U.S.D.A. Agric. Res. Serv. Techn. Bull. 1755: 1-408.</li>
<li>Johri, B.M., Ambegoakar, K.B. &amp; Srivastava, P.S. 1992: Comparative embryology of angiosperms. Vol 1 + 2. Springer Verlag, Berlin, Heidelberg, New York, Tokyo.</li>
<li>Kapil, R.N., Bor, J. &amp; Bouman, F. 1980: Seed appendages in angiosperms. I. Introduction. Bot. Jahrb. Syst. 101(4): 555-573.</li>
<li>Martin, A.C. 1946: The Comparative Internal Morphology of Seeds. American Midland Naturalist 36, 513-660</li>
<li>Roth, I. 1977: Fruits of angiosperms. Gebrüder Bornträger, Berlin &amp; Stuttgart. 675 pp.</li>
<li>Spjut, R.W. 1994: A systematic treatment of fruit types. Mem. New York Bot. Gard. 70:1-182.</li>
<li>Vaughan, J.G. 1970: The structure and utilisation of oil seeds. Chapman &amp; Hall, London, UK.</li>
<li>Werker, E. 1997: Seed anatomy. Gebrüder Bornträger, Berlin, Stuttgart (Encyclopedia of plant anatomy, Vol. 10, Part 3.)</li>
</ul>

</div> */
export const Route = createFileRoute('/definitions/morphology')({
  component: MorphologyPage,
})
